import React, { FC, useEffect, useState } from "react";
import { patientApi } from "../../../api/patientApi";
import { PatientDto } from "../../../endpoints";
import { useTranslation } from "react-i18next";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { RoutePaths } from "../../../routes/routePaths";
import { styled } from "@mui/material/styles";

const CustomToolbar = styled(GridToolbarContainer)(({ theme }) => ({
  justifyContent: "right",
  "& .MuiTextField-root": {
    padding: "10px",
  },
}));

const PAGE_SIZE = 20;

const PatientIndex: FC = () => {
  const [patients, setPatients] = useState<PatientDto[] | undefined>(undefined);
  const [totalPatients, setTotalPatients] = useState<number>();
  const [fullNameSearchQuery, setFullNameSearchQuery] = useState<string>("");
  const [phoneSearchQuery, setPhoneSearchQuery] = useState<string>("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const { t } = useTranslation();

  useEffect(() => {
    const run = async () => {
      const patientsPage =
        await patientApi.getAllPatientsPaginated(paginationModel);

      setPatients(patientsPage.content);
      setTotalPatients(patientsPage.totalElements);
    };
    void run();
  }, []);

  useEffect(() => {
    const run = async () => {
      // @ts-ignore
      const patients = await patientApi.searchPatients(
        fullNameSearchQuery,
        "FULL_NAME",
      );
      setPatients(patients);
    };

    if (fullNameSearchQuery) {
      void run();
    } else {
      void updatePaginatedPatients();
    }
  }, [fullNameSearchQuery]);

  useEffect(() => {
    const run = async () => {
      // @ts-ignore
      const patients = await patientApi.searchPatients(
        phoneSearchQuery,
        "PHONE",
      );
      setPatients(patients);
    };

    if (phoneSearchQuery) {
      void run();
    } else {
      void updatePaginatedPatients();
    }
  }, [phoneSearchQuery]);

  const handlePaginationModelChange = async (model: GridPaginationModel) => {
    const patientsPage = await patientApi.getAllPatientsPaginated(model);

    setTotalPatients(patientsPage.totalElements);
    setPaginationModel(model);
    setPatients(patientsPage.content);
  };

  const updatePaginatedPatients = async () => {
    const patientsPage =
      await patientApi.getAllPatientsPaginated(paginationModel);

    setPatients(patientsPage.content);
    setTotalPatients(patientsPage.totalElements);
  };

  const columns: GridColDef[] = [
    {
      headerName: t("fullname"),
      field: "fullname",
      width: 200,
      valueGetter: (params: GridValueGetterParams<PatientDto>) =>
        params.row.fullName,
      renderCell: (params: GridRenderCellParams<PatientDto>) => (
        <Typography>
          <Link to={RoutePaths.PATIENT_SHOW(params.row.id)}>
            {params.row.fullName}
          </Link>
        </Typography>
      ),
    },
    { headerName: t("email"), field: "email", width: 200 },
    { headerName: t("telephone"), field: "phone", width: 200 },
  ];

  return (
    <div>
      {patients && totalPatients && (
        <DataGrid
          sx={{
            padding: 1,
            boxShadow: 3,
            borderRadius: 4,
          }}
          rows={patients}
          paginationMode="server"
          rowCount={totalPatients}
          paginationModel={paginationModel}
          columns={columns}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[PAGE_SIZE]}
          autoHeight
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              children: (
                <>
                  <GridToolbarQuickFilter
                    value={phoneSearchQuery}
                    placeholder={t("search.by.phone")}
                    onChange={(event) => {
                      setPhoneSearchQuery(event.target.value);
                    }}
                    debounceMs={300}
                  />

                  <GridToolbarQuickFilter
                    value={fullNameSearchQuery}
                    placeholder={t("search.by.fullname")}
                    onChange={(event) => {
                      setFullNameSearchQuery(event.target.value);
                    }}
                    debounceMs={300}
                  />
                </>
              ),
            },
          }}
        />
      )}
    </div>
  );
};

export default PatientIndex;
